/**
 * Placeholder page for billing info collection during sign up
 */
import React from 'react';
import PropTypes from 'prop-types';
import { uiState } from 'react-redux-ui-state';
import { reduxForm, SubmissionError, Field } from 'redux-form';
import { injectStripe } from 'react-stripe-elements';
import { withStyles } from '@material-ui/styles';
import Typography from '@material-ui/core/Typography';

import classnames from 'classnames';
import { GENERIC_API_ERROR } from '../../../constants/errors';
import StripeInputWrapper from './StripeInputWrapper/StripeInputWrapper';
import BusyButton from '../../BusyButton/BusyButton';
import LoadingPlaceholder from '../../LoadingPlaceholder/LoadingPlaceholder';

const propTypes = {
  onStripeSubmit: PropTypes.func.isRequired,
  onStripeSuccess: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
  allowMultipleSubmissions: PropTypes.bool,
  buttonText: PropTypes.node.isRequired,
  // This could include any current or future-supported MUI button props
  buttonProps: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  stripe: PropTypes.shape({
    createSource: PropTypes.func.isRequired,
  }).isRequired,
  className: PropTypes.string,
  classes: PropTypes.shape({
    root: PropTypes.string.isRequired,
    submit: PropTypes.string.isRequired,
    hidden: PropTypes.string.isRequired,
  }).isRequired,
  ready: PropTypes.bool.isRequired,
  shouldReset: PropTypes.bool.isRequired,
  successMessage: PropTypes.string,
  setUiState: PropTypes.func.isRequired,
  submitting: PropTypes.bool.isRequired,
  pristine: PropTypes.bool.isRequired,
  valid: PropTypes.bool.isRequired,
  submitSucceeded: PropTypes.bool.isRequired,
  error: PropTypes.string,
  handleSubmit: PropTypes.func.isRequired,
  asyncValidate: PropTypes.func.isRequired,
  touch: PropTypes.func.isRequired,
  change: PropTypes.func.isRequired,
  reset: PropTypes.func.isRequired,
  innerText: PropTypes.shape({}),
  screen: PropTypes.string.isRequired,
};

const defaultProps = {
  onSuccess: null,
  allowMultipleSubmissions: true,
  buttonProps: null,
  className: null,
  error: null,
  successMessage: null,
  innerText: null,
};

export function UnstyledBillingForm({
  onStripeSubmit,
  onStripeSuccess,
  onSuccess,
  allowMultipleSubmissions,
  buttonProps,
  buttonText,
  stripe,
  classes,
  className,
  setUiState,
  submitting,
  pristine,
  valid,
  submitSucceeded,
  error,
  handleSubmit,
  asyncValidate,
  touch,
  change,
  reset,
  ready,
  shouldReset,
  successMessage,
  innerText,
  screen,
}) {
  const readyHandler = () => {
    setUiState({ ready: true });
  };

  const fieldChangeHandler = (obj) => {
    touch('stripe');
    change('stripe', true);
    asyncValidate('stripe', obj);
  };

  const resetHandler = () => {
    if (shouldReset) {
      setUiState({ shouldReset: false });
      reset();
    }
  };

  const submitHandler = () => {
    setUiState({ successMessage: null });
    return onStripeSubmit(stripe.createSource, { type: 'card' })
      .then((payload) => onStripeSuccess(payload.source.id))
      .then((payload) => {
        if (payload.error) {
          throw new SubmissionError();
        }
        if (onSuccess) {
          onSuccess();
        }
        if (allowMultipleSubmissions) {
          setUiState({
            shouldReset: true,
            successMessage: 'Success! Your payment information has been updated.',
          });
        }
      })
      .catch(() => {
        throw new SubmissionError({
          _error: GENERIC_API_ERROR,
        });
      });
  };

  const formStyle = {
    create: 'createFieldWrapper',
    profile: 'fieldWrapper',
  };

  return (
    <React.Fragment>
      {!ready && <LoadingPlaceholder />}
      <form
        onSubmit={handleSubmit(submitHandler)}
        className={classnames(
          screen === 'profile' ? classes.root : null,
          !ready ? classes.hidden : null,
          className,
        )}
      >
        <div className={classes[formStyle[screen]]}>
          <Field
            name="stripe"
            component={StripeInputWrapper}
            props={{
              onChange: fieldChangeHandler,
              onReset: resetHandler,
              onReady: readyHandler,
              shouldReset,
              screen,
            }}
          />
          {error && !submitting ? (
            <Typography color="error" className={classes.formStatus}>
              {error}
            </Typography>
          ) : null}
          {successMessage && pristine ? (
            <Typography color="secondary" className={classes.formStatus}>
              {successMessage}
            </Typography>
          ) : null}
        </div>
        {innerText}
        <BusyButton
          {...buttonProps}
          type="submit"
          className={classnames(buttonProps.className, classes.submit)}
          loading={submitting}
          mini
          disabled={!valid || (submitSucceeded && !allowMultipleSubmissions) || pristine}
        >
          {buttonText}
        </BusyButton>
      </form>
    </React.Fragment>
  );
}

UnstyledBillingForm.propTypes = propTypes;
UnstyledBillingForm.defaultProps = defaultProps;

export const asyncValidateHandler = (fields) =>
  new Promise((resolve, reject) => {
    const { stripe } = fields;
    if (stripe) {
      if (stripe.error) {
        // Redux form expects plain objects when rejecting
        reject({ stripe: stripe.error.message }); // eslint-disable-line prefer-promise-reject-errors
      }
      if (!stripe.complete) {
        // Redux form expects plain objects when rejecting
        reject({ stripe: true }); // eslint-disable-line prefer-promise-reject-errors
      }
    }
    resolve();
  });

const styles = (theme) => ({
  root: {
    marginTop: theme.spacing(2),
  },
  submit: {
    margin: theme.spacing(1),
    marginLeft: theme.spacing(1.5),
  },
  formStatus: {
    marginTop: theme.spacing(2),
  },
  hidden: {
    visibility: 'hidden',
    height: 0,
  },
  fieldWrapper: {
    width: '100%',
  },
  createFieldWrapper: {
    border: `1px solid ${theme.customColors.teal}`,
    borderTop: 'unset',
    padding: theme.spacing(1),
    width: '100%',
  },
  field: {
    margin: `${theme.spacing(2)}px 0`,
  },
});

export const uiConfig = {
  name: 'billingForm',
  state: () => ({
    ready: false,
    shouldReset: false,
    successMessage: null,
  }),
};

export const formConfig = {
  form: 'billingForm',
  destroyOnUnmount: true,
  initialValues: { stripe: null },
  shouldAsyncValidate: ({ trigger }) => trigger !== 'submit',
  asyncValidate: asyncValidateHandler,
  onSubmitFail: () => {},
};

const injectedForm = injectStripe(UnstyledBillingForm, { withRef: true });

export default reduxForm(formConfig)(uiState(uiConfig)(withStyles(styles)(injectedForm)));
